import SingleSlider from 'Components/Common/SingleSlider';
import RadioGroup from 'Components/input/RadioGroup/RadioGroup';
import { genders } from 'constants/campaign';
import React, { useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { DiscoveryCreatorFilters, DiscoveryCreatorGender } from 'types/creator-discovery';

const ENGAGEMENT_SCORE_MIN = 0;
const ENGAGEMENT_SCORE_MAX = 20;

const GENDER_OPTIONS = Object.entries(genders)
    .filter(([key]) => key !== 'U')
    .map(([key, value]) => ({ id: key, label: value }));

type GenderOption = 'A' | DiscoveryCreatorGender;

interface DiscoveryFiltersFormState {
    followers_min: string;
    followers_max: string;
    engagement_score: number;
    gender: GenderOption;
}

export interface DiscoveryFiltersModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentFilters: DiscoveryCreatorFilters;
    onApply: (filters: DiscoveryCreatorFilters) => void;
}

const filtersToForm = (filters: DiscoveryCreatorFilters): DiscoveryFiltersFormState => ({
    followers_min: filters.followers_count?.min != null ? String(filters.followers_count.min) : '',
    followers_max: filters.followers_count?.max != null ? String(filters.followers_count.max) : '',
    engagement_score: filters.engagement_score ?? ENGAGEMENT_SCORE_MIN,
    gender: filters.gender ?? 'A',
});

const buildAppliedFilters = (form: DiscoveryFiltersFormState): DiscoveryCreatorFilters => {
    const next: DiscoveryCreatorFilters = {};

    const followersMin = form.followers_min.trim();
    const followersMax = form.followers_max.trim();
    if (followersMin || followersMax) {
        next.followers_count = {
            ...(followersMin ? { min: Number(followersMin) } : {}),
            ...(followersMax ? { max: Number(followersMax) } : {}),
        };
    }

    if (form.engagement_score > ENGAGEMENT_SCORE_MIN) {
        next.engagement_score = form.engagement_score;
    }

    if (form.gender === 'M' || form.gender === 'F') {
        next.gender = form.gender;
    }

    return next;
};

const DiscoveryFiltersModal: React.FC<DiscoveryFiltersModalProps> = ({
    isOpen,
    onClose,
    currentFilters,
    onApply,
}) => {
    const [form, setForm] = useState<DiscoveryFiltersFormState>(() => filtersToForm(currentFilters));
    const [syncedOpen, setSyncedOpen] = useState(isOpen);

    if (isOpen && !syncedOpen) {
        setSyncedOpen(true);
        setForm(filtersToForm(currentFilters));
    } else if (!isOpen && syncedOpen) {
        setSyncedOpen(false);
    }

    const followersRangeInvalid =
        form.followers_min.trim() !== '' &&
        form.followers_max.trim() !== '' &&
        Number(form.followers_min) > Number(form.followers_max);

    const handleApply = (nextForm: DiscoveryFiltersFormState) => {
        const min = nextForm.followers_min.trim();
        const max = nextForm.followers_max.trim();
        if (min && max && Number(min) > Number(max)) return;
        onApply(buildAppliedFilters(nextForm));
        onClose();
    };

    return (
        <Modal isOpen={isOpen} toggle={onClose} centered className="creator-discovery-filters-modal">
            <ModalHeader toggle={onClose}>Filters</ModalHeader>
            <ModalBody>
                <div className="creator-discovery-filters-field">
                    <span className="creator-discovery-filters-label">Followers</span>
                    <div className="creator-discovery-filters-range">
                        <div>
                            <label className="creator-discovery-filters-sublabel" htmlFor="discovery-filter-followers-min">
                                Min
                            </label>
                            <input
                                id="discovery-filter-followers-min"
                                type="text"
                                inputMode="numeric"
                                className="form-control"
                                placeholder="e.g. 10000"
                                value={form.followers_min}
                                onChange={(e) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        followers_min: e.target.value.replace(/\D/g, ''),
                                    }))
                                }
                            />
                        </div>
                        <div>
                            <label className="creator-discovery-filters-sublabel" htmlFor="discovery-filter-followers-max">
                                Max
                            </label>
                            <input
                                id="discovery-filter-followers-max"
                                type="text"
                                inputMode="numeric"
                                className="form-control"
                                placeholder="e.g. 50000"
                                value={form.followers_max}
                                onChange={(e) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        followers_max: e.target.value.replace(/\D/g, ''),
                                    }))
                                }
                            />
                        </div>
                    </div>
                    {followersRangeInvalid && (
                        <p className="creator-discovery-filters-hint text-danger mb-0">
                            Minimum followers cannot be greater than maximum.
                        </p>
                    )}
                </div>
                <div className="creator-discovery-filters-field">
                    <label className="creator-discovery-filters-label" htmlFor="discovery-filter-engagement">
                        Minimum engagement score
                    </label>
                    <SingleSlider
                        min={ENGAGEMENT_SCORE_MIN}
                        max={ENGAGEMENT_SCORE_MAX}
                        value={form.engagement_score}
                        onChange={(engagement_score) =>
                            setForm((prev) => ({ ...prev, engagement_score }))
                        }
                    />
                    <p className="creator-discovery-filters-hint mb-0">
                        {form.engagement_score === ENGAGEMENT_SCORE_MIN
                            ? 'No minimum — drag to set a minimum score'
                            : `Minimum score: ${form.engagement_score}`}
                    </p>
                </div>
                <div className="creator-discovery-filters-field">
                    <label className="creator-discovery-filters-label" htmlFor="discovery-filter-gender">
                        Gender
                    </label>
                    <RadioGroup
                        key={String(isOpen)}
                        name="discovery-filter-gender"
                        options={GENDER_OPTIONS}
                        defaultValue={form.gender}
                        onChange={(gender) =>
                            setForm((prev) => ({ ...prev, gender: gender as GenderOption }))
                        }
                        selectFirstWhenNoValueProvided={false}
                    />
                </div>
            </ModalBody>
            <ModalFooter>
                <button
                    type="button"
                    className="theme-btn theme-btn-lg w-100 btn-primary-outlined"
                    onClick={() => handleApply(filtersToForm({}))}
                >
                    Reset
                </button>
                <button
                    type="button"
                    className="theme-btn theme-btn-lg w-100 btn-primary"
                    onClick={() => handleApply(form)}
                >
                    Apply
                </button>
            </ModalFooter>
        </Modal>
    );
};

export default DiscoveryFiltersModal;
